import React, { useCallback } from 'react';
import { connect, ErrorMessage, FormikContextType } from 'formik';
import styled from 'styled-components';

import { Switcher, Combobox } from '@shared/fields';
import { withScrollToError } from '../../../hocs/withScrollToError';
import { LabeledField } from '../../LabeledField';
import { StarRating } from '../../StarRating';
import Textareafield from '../../fields/Textareafield';
import { Checkbox } from '../../fields/Checkbox';
import { CheckboxLabel } from '../CheckboxLabel';
import Btn from '../../buttons/Btn';
import {
  BLUE_GREY, ERROR_COLOR, FONT_FAMILY_MAIN,
} from '../../../theme';
import { TextareaHints } from '../TextareaHints';
import media from '../../../utils/mediaQueries';
import { FunderReviewFormFootnote } from './FunderReviewFormFootnote';
import stores from '../../../stores';
import { FunderFormValues } from './types/FunderFormValues';
import Field from '../../fields/Field';
import { ReviewerType } from '../../../enums';

const Form = styled.form`
  font-size: 14px;
`;

const ErrorMessageContainer = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  color: ${ERROR_COLOR};
  font-size: 12px;
  line-height: 1.17;
  margin-top: 3px;
`;

const FieldComment = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: -0.1px;
  color: ${BLUE_GREY};
  margin-top: 8px;
`;

const ButtonContainer = styled.div`
  margin: 16px 0 24px;
  width: 242px;

  ${media.mobile`
    max-width: none;
  `}
`;

const GroupedField = styled.div`
  ${(props) => props.hidden && 'display: none;'}
  
  & + & {
    margin-top: 16px;
  }
`;

const FieldNote = styled.div`
  color: ${BLUE_GREY};
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: -0.1px;
  font-weight: 400;
`;

type FormProps = {
  setReviewerType(type: string): void;
  reviewersOptions: Array<{
    id: ReviewerType,
    name: string
  }>;
  reviewTextRef: React.RefObject<HTMLTextAreaElement>;
  userSchools?: Array<{
    id: string,
    title: string,
    placeId: string
  }>;
  isReferral?: boolean;
  minCommentLength?: number;
};

type FormikProps = {
  formik: FormikContextType<FunderFormValues>;
};

const ReviewForm = ({
  formik, reviewersOptions, setReviewerType, reviewTextRef, userSchools, isReferral, minCommentLength,
}: FormProps & FormikProps) => {
  const {
    values, errors, touched, handleChange, handleSubmit, handleBlur, isSubmitting, status, setFieldValue,
  } = formik;

  const handleReviewerTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>, setValue: (field: string, value: string) => void) => {
    setReviewerType(e.currentTarget.value);
    setValue(e.currentTarget.name, e.currentTarget.value);
  }, []);

  const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>, setValue: (field: string, value: string) => void) => {
    const { schoolReviewStore } = stores;
    const selectedSchool = userSchools.find((school) => school.id === e.currentTarget.value);
    schoolReviewStore.setPlaceId(selectedSchool.placeId);
    setValue(e.currentTarget.name, e.currentTarget.value);
  };

  return (
    <Form onSubmit={handleSubmit} data-testid="funder-review-form">
      {userSchools.length
        ? (
          <LabeledField label="Auto-école">
            <Combobox
              name="selectedSchoolId"
              width="100%"
              options={userSchools}
              onChange={(e) => handleSchoolChange(e, setFieldValue)}
              onBlur={handleBlur}
              value={values.selectedSchoolId || userSchools[0].id}
            />
          </LabeledField>
        )
        : ''}
      {!isReferral && (
        <LabeledField label="Vous êtes" isRequired error={errors.reviewerType && touched.reviewerType}>
          <Combobox
            name="reviewerType"
            width="100%"
            options={reviewersOptions}
            onChange={(e) => handleReviewerTypeChange(e, setFieldValue)}
            onBlur={handleBlur}
            value={values.reviewerType}
          />
        </LabeledField>
      )}
      <LabeledField isRequired label="Note globale" error={errors.stars && touched.stars}>
        <StarRating
          selectedStars={values.stars}
          onStarClick={handleChange}
          name="stars"
        />
        <ErrorMessage component={ErrorMessageContainer} name="stars" />
      </LabeledField>
      <LabeledField label="Offre de formation">
        <Switcher
          items={['1', '2', '3', '4', '5']}
          name="offer"
          activeItem={values.offer}
          onChange={handleChange}
        />
        <FieldComment>Notre offre de formation est-elle accessible et claire ?</FieldComment>
      </LabeledField>
      <LabeledField label="Devis">
        <Switcher
          items={['1', '2', '3', '4', '5']}
          name="estimate"
          activeItem={values.estimate}
          onChange={handleChange}
        />
        <FieldComment>Les devis transmis pour les prestations de formations étaient-ils suffisamment détaillés ?</FieldComment>
      </LabeledField>
      <LabeledField label="Documents administratifs">
        <Switcher
          items={['1', '2', '3', '4', '5']}
          name="documents"
          activeItem={values.documents}
          onChange={handleChange}
        />
        <FieldComment>Suite à une formation, avez-vous reçu de la part de notre organisme les documents administratifs qui vous sont nécessaires ?</FieldComment>
      </LabeledField>
      <LabeledField label="Délais">
        <Switcher
          items={['1', '2', '3', '4', '5']}
          name="indulgence"
          activeItem={values.indulgence}
          onChange={handleChange}
        />
        <FieldComment>Nos délais de traitement sont-ils conformes à vos attentes ?</FieldComment>
      </LabeledField>
      <LabeledField label="Facturation">
        <Switcher
          items={['1', '2', '3', '4', '5']}
          name="billing"
          activeItem={values.billing}
          onChange={handleChange}
        />
        <FieldComment>La facturation de la formation dispensée est-elle en adéquation avec le devis établi ?</FieldComment>
      </LabeledField>
      <LabeledField
        label="Commentaire"
        isRequired
        error={errors.comment && touched.comment}
        labelAddition={(
          <TextareaHints
            content="Parlez de la qualité des relations que vous avez eues avec l'auto-école, de la clarté du devis, de l'offre de formation et de la facturation."
            value={values.comment}
            minLength={minCommentLength || 200}
          />
        )}
      >
        <Textareafield
          name="comment"
          value={values.comment}
          onChange={handleChange}
          onBlur={handleBlur}
          valid={touched.comment && !errors.comment}
          textareaRef={reviewTextRef}
          resize="vertical"
        />
        <ErrorMessage component={ErrorMessageContainer} name="comment" />
        <FieldComment>
          <a href="/a-propos/conditions-particulieres-avis" target="_blank" rel="noopener noreferrer">
            Voir les critères de publication des avis
          </a>
          <div>Attention, les avis sont publiés et référencés, soyez courtois, ne citez pas le nom des personnes dans votre commentaire.</div>
        </FieldComment>
      </LabeledField>
      {!isReferral && (
        <LabeledField
          label="Contact"
          labelAddition={<FieldNote>Vous gardez l’anonymat et ne serez jamais spammé</FieldNote>}
          isRequired
          error={(errors.lastName && touched.lastName) || (errors.firstName && touched.firstName) || (errors.email && touched.email)}
        >
          <GroupedField>
            <Field
              placeholder="Nom"
              name="lastName"
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              valid={touched.lastName && !errors.lastName}
            />
            <ErrorMessage component={ErrorMessageContainer} name="lastName" />
          </GroupedField>
          <GroupedField>
            <Field
              placeholder="Prénom"
              name="firstName"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              valid={touched.firstName && !errors.firstName}
            />
            <ErrorMessage component={ErrorMessageContainer} name="firstName" />
          </GroupedField>
          <GroupedField>
            <Field
              placeholder="Email"
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              valid={touched.email && !errors.email}
            />
            <ErrorMessage component={ErrorMessageContainer} name="email" />
          </GroupedField>
        </LabeledField>
      )}
      <LabeledField label="">
        <Checkbox
          name="conditionsAccepted"
          label={<CheckboxLabel />}
          isChecked={values.conditionsAccepted}
          onChange={handleChange}
          error={errors.conditionsAccepted && touched.conditionsAccepted}
        />
        <ErrorMessage component={ErrorMessageContainer} name="conditionsAccepted" />

        <ButtonContainer>
          <Btn type="submit" text="Publier l'avis" isLoading={isSubmitting} disabled={isSubmitting} block name="submitReview" />
          {!!status && !status.success && <ErrorMessageContainer>Une erreur est survenue.</ErrorMessageContainer>}
          {errors.message && <ErrorMessageContainer>{errors.message}</ErrorMessageContainer>}
        </ButtonContainer>
      </LabeledField>
      <FunderReviewFormFootnote />
    </Form>
  );
};

ReviewForm.defaultProps = {
  userSchools: [],
  isReferral: false,
  minCommentLength: 200,
};

const FunderReviewFormComponent = withScrollToError(connect(ReviewForm));

export { FunderReviewFormComponent };
